import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { MailIcon, PhoneIcon, MapPinIcon, SendIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t
  } = useLanguage();
  return <section id="contacts" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-28">
      <div className="relative z-10 mx-auto max-w-6xl" data-id="element-29">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-12 sm:mb-16 text-center" data-id="element-30">
          <h2 className="text-3xl md:text-4xl font-bold" style={{
          color: 'var(--text-primary)'
        }} data-id="element-31">
            {t('contact.title')}
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12" data-id="element-32">
          {/* Left: Info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="flex flex-col gap-6" data-id="element-33">
            <div className="rounded-3xl border p-8 flex items-center gap-6" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-34">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: 'var(--bg-tertiary)'
            }} data-id="element-35">
                <MailIcon className="w-6 h-6" style={{
                color: 'var(--accent)'
              }} data-id="element-36" />
              </div>
              <div data-id="element-37">
                <p className="text-sm font-medium mb-1" style={{
                color: 'var(--text-muted)'
              }} data-id="element-38">
                  {t('contact.email')}
                </p>
                <p className="text-lg font-bold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-39">
                  info@itcluster.eu
                </p>
              </div>
            </div>

            <div className="rounded-3xl border p-8 flex items-center gap-6" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-40">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: 'var(--bg-tertiary)'
            }} data-id="element-41">
                <PhoneIcon className="w-6 h-6" style={{
                color: 'var(--accent)'
              }} data-id="element-42" />
              </div>
              <div data-id="element-43">
                <p className="text-sm font-medium mb-1" style={{
                color: 'var(--text-muted)'
              }} data-id="element-44">
                  {t('contact.phone')}
                </p>
                <p className="text-lg font-bold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-45">
                  +36 1 234 5678
                </p>
              </div>
            </div>

            <div className="rounded-3xl border p-8 flex items-center gap-6" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-46">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{
              backgroundColor: 'var(--bg-tertiary)'
            }} data-id="element-47">
                <MapPinIcon className="w-6 h-6" style={{
                color: 'var(--accent)'
              }} data-id="element-48" />
              </div>
              <div data-id="element-49">
                <p className="text-sm font-medium mb-1" style={{
                color: 'var(--text-muted)'
              }} data-id="element-50">
                  {t('contact.office')}
                </p>
                <p className="text-lg font-bold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-51">
                  Budapest, Hungary
                </p>
              </div>
            </div>
          </motion.div>

          {/* Right: Form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-52">
            <div className="h-full rounded-3xl border p-8 sm:p-10 flex flex-col" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-53">
              <h3 className="text-2xl font-bold mb-6" style={{
              color: 'var(--text-primary)'
            }} data-id="element-54">
                {t('contact.form.title')}
              </h3>
              <form onSubmit={e => e.preventDefault()} className="space-y-5 flex-1 flex flex-col" data-id="element-55">
                <div data-id="element-56">
                  <input type="text" required className="w-full rounded-xl border px-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('contact.form.name')} data-id="element-57" />
                </div>
                <div data-id="element-58">
                  <input type="email" required className="w-full rounded-xl border px-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('contact.form.email')} data-id="element-59" />
                </div>
                <div className="flex-1" data-id="element-60">
                  <textarea required rows={4} className="w-full h-full resize-none rounded-xl border px-4 py-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30" style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)'
                }} placeholder={t('contact.form.msg')} data-id="element-61" />
                </div>
                <button type="submit" className="group w-full rounded-xl py-4 font-bold text-base transition-all hover:scale-[1.02] flex items-center justify-center gap-2" style={{
                backgroundColor: 'var(--accent)',
                color: '#FFFFFF'
              }} data-id="element-62">
                  {t('contact.form.send')}
                  <SendIcon className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" data-id="element-63" />
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}